/**
 * 🚀 [Phase v13.47 — 2026-07-18] Helpers messagerie.
 *
 * Utilitaires purs (sans dépendance React) réutilisables :
 *   - `compressImage` : compression JPEG côté client via Canvas API.
 *       Cible ~500 Ko pour photo standard, plafond 1920×1080, qualité 0.75.
 *       Fallback pass-through pour formats non-image ou sans support Canvas.
 *   - `formatTimestampFR` : date/heure exacte FR-CA pour tooltip (title HTML).
 *   - `computeReadStatus` : état ✓/✓✓ à partir de `lu_par` et `participants`.
 *
 * FR-CA strict pour toutes les chaînes retournées.
 */

/**
 * Compresse une image via Canvas → JPEG qualité 0.75.
 * @param {File} file — Le fichier à traiter (image/*). Non-image ⇒ pass-through.
 * @param {{maxWidth?: number, maxHeight?: number, quality?: number}} options
 * @returns {Promise<{nom: string, type: string, data_b64: string, taille: number, taille_originale?: number}>}
 */
export async function compressImage(file, options = {}) {
  const maxWidth = options.maxWidth ?? 1920;
  const maxHeight = options.maxHeight ?? 1080;
  const quality = options.quality ?? 0.75;

  // Fallback : non-image ou pas de canvas → lire directement en base64
  const readAsBase64 = () => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target.result;
      const base64 = dataUrl.split(',')[1];
      resolve({
        nom: file.name,
        type: file.type,
        data_b64: base64,
        taille: file.size,
      });
    };
    reader.onerror = () => reject(new Error('Lecture du fichier échouée'));
    reader.readAsDataURL(file);
  });

  if (!file.type || !file.type.startsWith('image/')) {
    return readAsBase64();
  }
  // Certains formats ne se recompressent pas bien (SVG, GIF animé)
  if (file.type === 'image/svg+xml' || file.type === 'image/gif') {
    return readAsBase64();
  }
  if (typeof document === 'undefined' || !document.createElement('canvas').getContext) {
    return readAsBase64();
  }

  const dataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (ev) => resolve(ev.target.result);
    reader.onerror = () => reject(new Error('Lecture du fichier échouée'));
    reader.readAsDataURL(file);
  });

  const img = await new Promise((resolve, reject) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = () => reject(new Error('Décodage de l\'image échoué'));
    el.src = dataUrl;
  });

  let { width, height } = img;
  const ratio = Math.min(maxWidth / width, maxHeight / height, 1);
  width = Math.round(width * ratio);
  height = Math.round(height * ratio);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, width, height);

  // Recompresse en JPEG (plus petit que PNG). Force type même si l'origine
  // était PNG — le poids brut prime sur la transparence pour la messagerie.
  const outputType = 'image/jpeg';
  const compressedDataUrl = canvas.toDataURL(outputType, quality);
  const compressedBase64 = compressedDataUrl.split(',')[1];
  // Taille approximative en octets : base64 = 4/3 * bytes ⇒ bytes ≈ len * 3/4
  const compressedSize = Math.floor(compressedBase64.length * 0.75);

  // Sécurité : si la compression alourdit le fichier (photo déjà optimisée),
  //   on retourne l'original.
  if (compressedSize >= file.size) {
    return readAsBase64();
  }

  // Rename : .jpg si l'origine n'était pas déjà JPEG
  const nom = file.type === outputType
    ? file.name
    : file.name.replace(/\.[^.]+$/, '') + '.jpg';

  return {
    nom,
    type: outputType,
    data_b64: compressedBase64,
    taille: compressedSize,
    taille_originale: file.size,
  };
}

/**
 * Formate une date ISO en date/heure FR-CA lisible pour un tooltip.
 * Ex : "17 juillet 2026 à 14:32".
 * @param {string} iso
 * @returns {string}
 */
export function formatTimestampFR(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const datePart = new Intl.DateTimeFormat('fr-CA', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(d);
    const timePart = new Intl.DateTimeFormat('fr-CA', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(d);
    return `${datePart} à ${timePart}`;
  } catch {
    return '';
  }
}

/**
 * Calcule le statut « lu » d'un message pour affichage ✓/✓✓ style WhatsApp.
 *
 * @param {{sender_id: string, lu_par?: string[]}} msg
 * @param {{participants?: string[], is_group?: boolean}} conv
 * @returns {{delivered: boolean, read: boolean, readByNames: string[]}}
 *   - `delivered=true` si le message existe en DB (toujours vrai côté frontend)
 *   - `read=true` si au moins un destinataire (≠ sender) a lu
 *   - `readByNames` liste optionnelle des noms (pour tooltip — vide si non fourni)
 */
export function computeReadStatus(msg, conv, participantNoms = {}) {
  if (!msg || !conv) return { delivered: true, read: false, readByNames: [] };
  const senderId = msg.sender_id;
  const luPar = Array.isArray(msg.lu_par) ? msg.lu_par : [];
  const otherReaders = luPar.filter(uid => uid && uid !== senderId);
  const readByNames = otherReaders
    .map(uid => participantNoms[uid])
    .filter(Boolean);
  return {
    delivered: true,
    read: otherReaders.length > 0,
    readByNames,
  };
}
